<div [class.read-only-mode]="isReadOnly()">
  <!-- Toast для сообщений -->
  <p-toast role="status" aria-live="polite" aria-atomic="true" />

  <!-- Live Region for Screen Readers -->
  <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
    {{ liveRegionMessage() }}
  </div>

  <!-- Instructions for accessibility -->
  @if (!isReadOnly()) {
    <div class="surface-100 border-round p-3 mb-4" role="region" aria-labelledby="form-instructions-title">
      <p id="form-instructions-title" class="text-sm text-700 m-0">
        <i class="pi pi-info-circle mr-2" aria-hidden="true"></i>
        <strong>Istruzioni:</strong> Compila tutti i campi obbligatori contrassegnati con asterisco (*). Nella sezione "Sede Operativa" indica se la sede legale è anche operativa; in caso contrario aggiungi almeno una sede operativa.
      </p>
    </div>
  }

  <!-- SECTION 1: INFORMAZIONI SPECIFICHE -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-info">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-info-circle text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-info" class="text-2xl font-semibold m-0 text-900">Informazioni Specifiche</h2>
        <p class="text-600 m-0 mt-1">Dati professionali e iscrizioni</p>
      </div>
    </div>

    <div class="formgrid grid">
      <!-- Activity Start Date -->
      <div class="field col-12 md:col-4">
        <label for="activity-start-date" class="font-semibold block mb-2">
          Inizio Attività dal
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <p-datepicker
          id="activity-start-date"
          [ngModel]="activityStartDate()"
          (ngModelChange)="activityStartDate.set($event)"
          dateFormat="dd/mm/yy"
          [maxDate]="today"
          [showIcon]="true"
          [showButtonBar]="true"
          placeholder="Seleziona data"
          inputId="activity-start-date-input"
          [attr.aria-label]="'Data inizio attività'"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldTouched('activityStartDate') && !activityStartDate()"
          styleClass="w-full"
          [disabled]="isReadOnly()"
          (onBlur)="markFieldTouched('activityStartDate')"
        />
        @if (isFieldTouched('activityStartDate') && !activityStartDate()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- National Registration Number -->
      <div class="field col-12 md:col-4">
        <label for="national-registration" class="font-semibold block mb-2">
          Numero iscrizione al Ruolo Periti Assicurativi - Consap
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <input
          id="national-registration"
          type="text"
          pInputText
          [ngModel]="nationalRegistrationNumber()"
          (ngModelChange)="nationalRegistrationNumber.set($event)"
          placeholder="Inserisci numero iscrizione"
          class="w-full"
          [disabled]="isReadOnly()"
          (blur)="markFieldTouched('nationalRegistrationNumber')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldTouched('nationalRegistrationNumber') && !nationalRegistrationNumber()"
        />
        @if (isFieldTouched('nationalRegistrationNumber') && !nationalRegistrationNumber()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Companies Worked For -->
      <div class="field col-12">
        <label for="companies-worked" class="font-semibold block mb-2">
          Imprese per le quali lavora
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <input
          id="companies-worked"
          type="text"
          pInputText
          [ngModel]="companiesWorkedFor()"
          (ngModelChange)="companiesWorkedFor.set($event)"
          placeholder="Inserisci le imprese"
          class="w-full"
          [disabled]="isReadOnly()"
          (blur)="markFieldTouched('companiesWorked For')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldTouched('companiesWorkedFor') && !companiesWorkedFor()"
        />
        @if (isFieldTouched('companiesWorkedFor') && !companiesWorkedFor()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Operating Territories -->
      <div class="field col-12">
        <label for="operating-territories" class="font-semibold block mb-2">
          Territorio operativo
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        @if (isReadOnly()) {
          <!-- In sola lettura mostriamo i territori come chip di testo: il
               p-multiselect disabilitato non rende i chip selezionati. -->
          <div id="operating-territories" class="flex flex-wrap gap-2">
            @for (label of operatingTerritoriesLabels(); track label) {
              <span class="inline-flex align-items-center surface-200 text-900 border-round px-2 py-1 text-sm">{{ label }}</span>
            } @empty {
              <span class="text-600">—</span>
            }
          </div>
        } @else {
          <p-multiselect
            id="operating-territories"
            [ngModel]="operatingTerritories()"
            (ngModelChange)="operatingTerritories.set($event)"
            [options]="provinceOptions()"
            optionLabel="label"
            optionValue="value"
            placeholder="Seleziona Provincia"
            [filter]="true"
            [showToggleAll]="true"
            [showClear]="true"
            display="chip"
            [maxSelectedLabels]="provinceOptions().length"
            styleClass="w-full"
            [disabled]="isReadOnly()"
            (blur)="markFieldTouched('operatingTerritories')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('operatingTerritories') && (!operatingTerritories() || operatingTerritories().length === 0)"
          />
          @if (isFieldTouched('operatingTerritories') && (!operatingTerritories() || operatingTerritories().length === 0)) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        }
      </div>
    </div>
  </section>

  <!-- SECTION 2: SEDE OPERATIVA (multi-sede) -->
  <app-operational-sites-section
    [sameAsLegal]="operatingSameAsLegal()"
    (sameAsLegalChange)="operatingSameAsLegal.set($event)"
    [sites]="operatingSites()"
    (sitesChange)="operatingSites.set($event)"
    [readonly]="isReadOnly()"
    [formSubmitted]="formSubmitted()"
  />

  <!-- SECTION 3 (Collaboratori) rimossa: i collaboratori sono ora gestiti nello step
       unificato "Impiegati Amministrativi e Collaboratori". -->

  <!-- Action Buttons (hidden in review mode) -->
  @if (!professionalIdInput()) {
  <div class="flex justify-content-end gap-3 mt-5">
    <button
      pButton
      type="button"
      label="Salva Bozza"
      icon="pi pi-save"
      class="p-button-outlined"
      [attr.aria-label]="'Salva i dati come bozza senza validazione'"
      pTooltip="Salva i dati inseriti come bozza. Non è richiesta la compilazione completa."
      tooltipPosition="top"
      (click)="saveDraft()"
    ></button>
    <button
      pButton
      type="button"
      label="Salva e continua"
      icon="pi pi-arrow-right"
      iconPos="right"
      [attr.aria-label]="'Salva i dati e procedi alla sezione successiva. Richiede la compilazione di tutti i campi obbligatori.'"
      pTooltip="Valida e salva i dati, poi prosegui. Tutti i campi obbligatori devono essere compilati."
      tooltipPosition="top"
      (click)="saveAndContinue()"
    ></button>
  </div>
  }
</div>
